const fallbackDefaultCoreID = "cor_e7eec4dce06f5a03a1b5418e0c5cbd7e"; // C0Pro-S+
const fallbackDefaultReferenceCoreID = "cor_f8e4d85980b458bda1542f7a1067ebcc"; // C0Pro-Reference

import { sdk } from "@/js/sdk";
import type { CoreDetails } from "@signaloid/cloud-compute-engine-client";
import { defineStore } from "pinia";
import { useUserStore } from "@/stores/user";
import { checkCoreRequirements, toStringWithSIMagnitude } from "@/js/utilities";

import {
	Core,
	CoreClass,
	CoreClassE,
	CoreClassObject,
	CoreMicroarchitecture,
	CoreCorrelationTypeCodeE,
	CoreCorrelationTypeE,
	CoreRequirementMatch,
	CoreMicroarchitectureObject,
	CoreCorrelationTypeObject,
	CoreMicroarchitectureE,
} from "@/types/api/cores";
import { RemoteAccessState } from "@/types/general";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { withinLimit } from "@/js/tierconfig";

// SDK returns CoreDetails (server-shaped); the app's Core type adds
// client-side flags (Immutable/Disabled/Hidden/Deprecated) which subsequent
// store actions compute based on tier and limits. Initialize to false so flag
// logic has a starting point.
function hydrateRemoteCore(remote: CoreDetails): Core {
	return {
		...remote,
		Immutable: false,
		Disabled: false,
		Hidden: false,
		Deprecated: false,
	};
}

const kJupiterEnabled: boolean = import.meta.env.VITE_ENABLE_JUPITER == "true";

/* Retired classes: the backend still serves existing cores, but they are no longer usable. */
const retiredCoreClasses: CoreClass[] = [CoreClassE.C0];

const SpecialMicroarchitecturesE = {
	Bypass: CoreMicroarchitectureE.Bypass,
	Reference: CoreMicroarchitectureE.Reference,
} as const;

function correlationTrackingMethodsFor(microarchitectures: CoreMicroarchitectureObject[]): CoreCorrelationTypeObject[] {
	return [
		{ Name: CoreCorrelationTypeE.Disabled, Code: CoreCorrelationTypeCodeE.Disabled },
		{ Name: CoreCorrelationTypeE.Autocorrelation, Code: CoreCorrelationTypeCodeE.Autocorrelation },
	].map((method) => ({
		...method,
		Experimental: false,
		SupportedMicroarchitectures: microarchitectures
			.filter((am) => am.Support.CorrelationTracking.includes(method.Code))
			.map((am) => am.Name),
	}));
}

type StoreState = {
	localCoresList: undefined | Core[];
	remoteCoresList: undefined | Core[];
	defaultCoresList: undefined | Core[];
	defaultCoreID: string | undefined;
	defaultReferenceCoreID: string | undefined;
	availableCorrelationTrackingMethods: CoreCorrelationTypeObject[];
	defaultCoreUpdateStatus: RemoteAccessState;
	defaultRefCoreUpdateStatus: RemoteAccessState;
	defaultCoreFetchStatus: RemoteAccessState;
	userCoreFetchStatus: RemoteAccessState;
	preferencesFetchStatus: RemoteAccessState;
	coresFilteredCoresByUserTier: undefined | Core[];
	userTierResolutionFailed: boolean;
	availableMicroarchitectures: CoreMicroarchitectureObject[];
	availableCoreClasses: CoreClassObject[];
	userCoresFetchInFlight: Promise<void> | undefined;
	defaultCoresFetchInFlight: Promise<void> | undefined;
};

export const useCoresStore = defineStore("cores", {
	state: (): StoreState => ({
		localCoresList: [],
		remoteCoresList: undefined,
		defaultCoresList: undefined,
		defaultCoreID: undefined,
		defaultReferenceCoreID: undefined,
		availableCorrelationTrackingMethods: [],
		defaultCoreUpdateStatus: { loading: false, error: false, message: "" },
		defaultRefCoreUpdateStatus: { loading: false, error: false, message: "" },
		defaultCoreFetchStatus: { loading: false, error: false, message: "" },
		userCoreFetchStatus: { loading: false, error: false, message: "" },
		preferencesFetchStatus: { loading: false, error: false, message: "" },
		coresFilteredCoresByUserTier: undefined,
		userTierResolutionFailed: false,
		availableMicroarchitectures: [],
		availableCoreClasses: [],
		userCoresFetchInFlight: undefined,
		defaultCoresFetchInFlight: undefined,
	}),
	getters: {
		loadingCores: (state) => {
			return (
				state.userCoreFetchStatus.loading ||
				state.defaultCoreFetchStatus.loading ||
				state.preferencesFetchStatus.loading
			);
		},
		loadError(state): boolean {
			return state.userCoreFetchStatus.error || state.defaultCoreFetchStatus.error;
		},
		allCoresList(state): Core[] {
			const coresList: Core[] = [];
			if (state.defaultCoresList) {
				coresList.push(...state.defaultCoresList);
			}
			if (state.localCoresList) {
				coresList.push(...state.localCoresList);
			}
			if (state.remoteCoresList) {
				coresList.push(...state.remoteCoresList);
			}
			return coresList;
		},
		allDefaultCores(state): Core[] {
			const coresList: Core[] = [];
			if (state.defaultCoresList) {
				coresList.push(...state.defaultCoresList);
			}
			return coresList;
		},
		filteredCoresList(state): Core[] {
			/*
			 *	Filtered cores are cores with the Hidden/Immutable/Disabled
			 *	fields correctly populated.
			 */
			const coresList: Core[] = [];
			if (state.coresFilteredCoresByUserTier) {
				coresList.push(...state.coresFilteredCoresByUserTier);
			}
			return coresList;
		},
		filteredVisibleCoresList(): Core[] {
			return this.filteredCoresList.filter((c) => {
				return !c.Hidden;
			});
		},
		filteredVisibleReferenceCoresList(): Core[] {
			return this.filteredVisibleCoresList.filter((c) => {
				return c?.Microarchitecture === SpecialMicroarchitecturesE.Reference;
			});
		},
		filteredVisibleNonReferenceCoresList(): Core[] {
			return this.filteredVisibleCoresList.filter((c) => {
				return c?.Microarchitecture != SpecialMicroarchitecturesE.Reference;
			});
		},
		correlationTrackingMethodByCode: (state) => (code: string) => {
			return state.availableCorrelationTrackingMethods.find((m) => {
				return m.Code == code;
			});
		},
		correlationTrackingSupportedByCore:
			(state) =>
			(microarchitecture: string): CoreCorrelationTypeObject[] => {
				return state.availableCorrelationTrackingMethods.filter((m) => {
					return m.SupportedMicroarchitectures.includes(microarchitecture);
				});
			},
		customPrecisionSupportedByCore:
			(state) =>
			(microarchitecture: string): boolean => {
				const filteredArch = state.availableMicroarchitectures.filter((am) => am.Name === microarchitecture);

				return filteredArch.length > 0 && filteredArch[0].Support.CustomPrecision;
			},
		coreMicroarchitectureStringFormat:
			() =>
			(microarchitecture: string, precision: number | undefined): string => {
				const precisionStr = toStringWithSIMagnitude(precision, "");

				switch (microarchitecture) {
					case SpecialMicroarchitecturesE.Bypass:
						return `${microarchitecture}`;
					default:
						if (precision === undefined) {
							console.warn(`Missing precision for microarchitecture ${microarchitecture}`);
							return `${microarchitecture}`;
						}
						return `${microarchitecture}-${precisionStr}`;
				}
			},
		microarchitecturesSupportedByCoreClass:
			(state) =>
			(coreClass: CoreClass): CoreMicroarchitectureObject[] => {
				const coreClassObject = state.availableCoreClasses.find((c: CoreClassObject) => c.Name === coreClass);

				if (!coreClassObject) {
					return [];
				}

				/*
				 *	Also check if core is enabled
				 */
				return state.availableMicroarchitectures.filter((m) => {
					return coreClassObject.SupportedMicroarchitectures.includes(m.Name) && !m.Disabled;
				});
			},
		defaultCore(): Core | undefined {
			if (!this.allCoresList || this.allCoresList.length === 0) {
				return undefined;
			}
			if (!this.defaultCoreID) {
				return undefined;
			}
			let core = this.allCoresList.find((core) => {
				return core.CoreID === this.defaultCoreID;
			});
			if (!core) {
				core = this.allCoresList[0];
			}
			return core;
		},

		defaultReferenceCore(): Core | undefined {
			if (!this.defaultReferenceCoreID) {
				return undefined;
			}
			let core = this.filteredVisibleReferenceCoresList.find((core) => {
				return core.CoreID === this.defaultReferenceCoreID;
			});
			if (!core) {
				core = this.filteredVisibleReferenceCoresList[0];
			}
			return core;
		},
		validateCore() {
			return (activeCore: Core): Core | undefined => {
				if (activeCore) {
					// * Check if the specified core is valid
					if (this.allCoresList.find((x) => x.CoreID === activeCore.CoreID)) {
						return activeCore;
					}
					console.warn(`Specified core ${activeCore?.Name} (CoreID=${activeCore?.CoreID}) cannot be found`);
				}

				/**
				 *	If argument was falsy or specified core is not found, then return the pre-specified default core.
				 */
				console.warn("Resetting to default core: ??? -> ", this.defaultCore?.Name);
				return this.defaultCore;
			};
		},
		getCoreByID: () => (coreID: string, coresList: Core[]) => {
			return coresList.find((x) => {
				return x.CoreID === coreID;
			});
		},
		coresSatisfyMinConfig() {
			return (minConfig: Partial<Core>) => {
				return Object.assign(
					{},
					...this.allCoresList.map((core) => {
						// eslint-disable-next-line prefer-const
						let output = {};
						const requirementMatch = checkCoreRequirements(core, minConfig) as CoreRequirementMatch;
						for (const key in requirementMatch) {
							if (requirementMatch[key] == false) {
								output[core.CoreID] = false;
								return output;
							}
						}
						output[core.CoreID] = true;
						return output;
					}),
				);
			};
		},
		getSmallestDefaultCoreForDiscoveringVariables(): Core | undefined {
			const discoverVariablesAllowedCores = this.allCoresList.filter(
				(core) =>
					core.Microarchitecture === "Athens" &&
					core.Class === CoreClassE.C0Pro &&
					!core.Disabled &&
					!core.Deprecated,
			);

			return discoverVariablesAllowedCores.reduce<Core | undefined>((coreToReturn, core) => {
				// A candidate with undefined precision always loses to the next option.
				if (!coreToReturn || coreToReturn.Precision === undefined) {
					return core;
				}

				if (core.Precision !== undefined && core.Precision < coreToReturn.Precision) {
					return core;
				}

				return coreToReturn;
			}, undefined);
		},
		getCustomCores(state): Core[] {
			return state.remoteCoresList ? state.remoteCoresList : [];
		},
	},
	actions: {
		applyAccountCoreCapabilities() {
			const userRecord = useUserStore().currentUserObjectFromDB;

			if (userRecord?.Microarchitectures) {
				this.availableMicroarchitectures = userRecord.Microarchitectures.map((microarchitecture, index) => ({
					ID: index + 1,
					Name: microarchitecture.Name as CoreMicroarchitecture,
					Support: {
						CorrelationTracking: microarchitecture.CorrelationTracking,
						CustomPrecision: microarchitecture.CustomPrecision,
						DistributionTracking: microarchitecture.DistributionTracking,
					},
					Experimental: microarchitecture.Name === CoreMicroarchitectureE.Jupiter,
					Disabled: microarchitecture.Name === CoreMicroarchitectureE.Jupiter && !kJupiterEnabled,
					Restricted: false,
				}));
				this.availableCorrelationTrackingMethods = correlationTrackingMethodsFor(
					this.availableMicroarchitectures,
				);
			}

			if (userRecord?.CoreClasses) {
				this.availableCoreClasses = userRecord.CoreClasses.map((coreClass) => ({
					Name: coreClass.Name as CoreClass,
					Enabled: true,
					Creatable: coreClass.Name === CoreClassE.C0Pro,
					Experimental: false,
					SupportedMicroarchitectures: coreClass.SupportedMicroarchitectures,
				}));
			}

			if (this.defaultCoresList) {
				this.setDefaultCoresList(this.defaultCoresList);
			}
		},
		setDefaultCoresList(defaultCoresList: Core[]) {
			// sort cores by non ref cores, ref cores, then bypass,
			// non-ref cores are sorted by precision and then by name

			const isDistTracking = (core: Core) =>
				this.availableMicroarchitectures.some(
					(am) => am.Name === core.Microarchitecture && am.Support.DistributionTracking,
				);

			const distTrackingCores = defaultCoresList.filter((core) => isDistTracking(core));

			const nonDistTrackingCores = defaultCoresList.filter((core) => !isDistTracking(core));

			const distTrackingCoresWithoutPrecision: string[] = [];

			for (const core of distTrackingCores) {
				if (core.Precision === undefined) {
					distTrackingCoresWithoutPrecision.push(core.Name);
				}
			}

			if (distTrackingCoresWithoutPrecision.length > 0) {
				const badCores = distTrackingCoresWithoutPrecision.join(", ");
				console.warn(`All distTrackings should have precision. Exceptions are: "${badCores}"`);
			}

			distTrackingCores.sort((a, b) => {
				// sort by precision (flush undefined precision to 0)

				const precisionDifference = (a.Precision || 0) - (b.Precision || 0);

				if (precisionDifference != 0) {
					return precisionDifference;
				}

				// if the precision is the same, then sort by name
				const nameA = a.Name.toLowerCase(); // ignore upper and lowercase
				const nameB = b.Name.toLowerCase(); // ignore upper and lowercase
				if (nameA < nameB) {
					return -1;
				}
				if (nameA > nameB) {
					return 1;
				}
				return 0;
			});

			this.defaultCoresList = [...distTrackingCores, ...nonDistTrackingCores];
		},
		setRemoteUserCoresList(remoteCoresList: Core[]) {
			this.remoteCoresList = remoteCoresList;
		},
		removeCoreFromLocalCoresList(incomingCoreData: Core) {
			if (!this.localCoresList) {
				return;
			}
			const coreIndex = this.localCoresList.findIndex((x) => x.CoreID === incomingCoreData.CoreID);
			this.localCoresList.splice(coreIndex, 1);
			localStorage.setItem("signaloid-local-user-cores-list", JSON.stringify(this.localCoresList));
		},
		async setDefaultCore(coreID: string, syncWithRemote: boolean) {
			this.defaultCoreUpdateStatus.loading = true;
			const coreInCoresStore = this.getCoreByID(coreID, this.allCoresList);
			if (coreInCoresStore) {
				this.defaultCoreID = coreInCoresStore.CoreID;
			} else {
				console.warn(
					`Specified default core not found: ${coreID}. Sticking with existing default: ${this.defaultCoreID}`,
				);
				return;
			}

			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await authUserStore.syncUserPreferenceWithRemote({
					Execution_DefaultCore: this.defaultCoreID,
				});
			}
			this.defaultCoreUpdateStatus.loading = false;
		},
		async setDefaultReferenceCore(coreID: string, syncWithRemote: boolean) {
			this.defaultRefCoreUpdateStatus.loading = true;
			const coreInCoresStore = this.getCoreByID(coreID, this.filteredVisibleReferenceCoresList);
			if (coreInCoresStore) {
				this.defaultReferenceCoreID = coreInCoresStore.CoreID;
			} else {
				console.warn(
					`Specified default reference core not found: ${coreID}. Sticking with existing default: ${this.defaultReferenceCoreID}`,
				);
				return;
			}

			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await authUserStore.syncUserPreferenceWithRemote({
					Execution_DefaultReferenceCore: this.defaultReferenceCoreID,
				});
			}
			this.defaultRefCoreUpdateStatus.loading = false;
		},
		async fetchUserCoresFromRemote() {
			this.userCoresFetchInFlight ??= (async () => {
				try {
					this.userCoreFetchStatus.loading = true;
					const resp = await sdk.cores.list();
					const remoteCores: Core[] = (resp.Cores ?? []).map((core) => {
						const hydrated = hydrateRemoteCore(core);
						hydrated.CorrelationTracking =
							hydrated.CorrelationTracking.charAt(0).toUpperCase() +
							hydrated.CorrelationTracking.slice(1);
						return hydrated;
					});
					this.setRemoteUserCoresList(remoteCores);
					this.userCoreFetchStatus.error = false;
				} catch (error) {
					this.userCoreFetchStatus.error = true;
					monitoringCaptureError(error, "Fetch user cores from cloud");
				} finally {
					this.userCoreFetchStatus.loading = false;
				}
			})().finally(() => {
				this.userCoresFetchInFlight = undefined;
			});

			await this.userCoresFetchInFlight;
		},
		async fetchDefaultCoresFromRemote() {
			this.defaultCoresFetchInFlight ??= (async () => {
				try {
					this.defaultCoreFetchStatus.loading = true;
					const resp = await sdk.cores.list({ default: true });
					const remoteCores: Core[] = (resp.Cores ?? []).map(hydrateRemoteCore);
					this.setDefaultCoresList(remoteCores);
					this.defaultCoreFetchStatus.error = false;
				} catch (error) {
					this.defaultCoreFetchStatus.error = true;
					monitoringCaptureError(error, "Fetch default cores from cloud");
				} finally {
					this.defaultCoreFetchStatus.loading = false;
				}
			})().finally(() => {
				this.defaultCoresFetchInFlight = undefined;
			});

			await this.defaultCoresFetchInFlight;
		},
		resetCoreIdIfRestricted(candidateCoreID: string | undefined) {
			if (!candidateCoreID) {
				return fallbackDefaultCoreID;
			}
			const listOfUnusableCores = this.coresFilteredCoresByUserTier?.filter((c) => c.Disabled || c.Deprecated);
			const listOfUnusableCoreIds = listOfUnusableCores?.map((c) => c.CoreID);

			// Force change preference to default core if out of tier or deprecated.
			if (listOfUnusableCoreIds?.includes(candidateCoreID)) {
				return fallbackDefaultCoreID;
			}
			return candidateCoreID;
		},
		async resetEditorCoreIfRestricted(candidateCoreID: string, fallbackCoreID: string) {
			const listOfUnusableCores = await this.getUnusableCoresByUserTier();
			const listOfUnusableCoreIds = listOfUnusableCores?.map((c) => c.CoreID);

			// Force change preference to default core if out of tier or deprecated.
			if (listOfUnusableCoreIds?.includes(candidateCoreID)) {
				return fallbackCoreID;
			}
			return candidateCoreID;
		},
		async initialiseExecutionCoresFromRemote() {
			this.preferencesFetchStatus.loading = true;
			try {
				// Each fetch action manages its own loadError flag.
				await Promise.allSettled([this.fetchDefaultCoresFromRemote(), this.fetchUserCoresFromRemote()]);
				await this.getCoresFilteredCoresByUserTier();

				const authUserStore = useUserStore();
				const preferencesFromDB = await authUserStore.getCurrentUserPreferences();
				if (preferencesFromDB) {
					const listOfUnusableCores = await this.getUnusableCoresByUserTier();
					const listOfUnusableCoreIds = listOfUnusableCores?.map((c) => c.CoreID);

					let candidateDefaultCoreID = preferencesFromDB?.Execution_DefaultCore
						? preferencesFromDB?.Execution_DefaultCore
						: fallbackDefaultCoreID;

					// Force change preference to default core if out of tier or deprecated.
					if (listOfUnusableCoreIds?.includes(candidateDefaultCoreID)) {
						candidateDefaultCoreID = fallbackDefaultCoreID;
					}

					const candidateDefaultReferenceCoreID = preferencesFromDB?.Execution_DefaultReferenceCore
						? preferencesFromDB?.Execution_DefaultReferenceCore
						: fallbackDefaultReferenceCoreID;
					this.setDefaultCore(
						candidateDefaultCoreID,
						candidateDefaultCoreID !== preferencesFromDB?.Execution_DefaultCore,
					);
					this.setDefaultReferenceCore(
						candidateDefaultReferenceCoreID,
						candidateDefaultReferenceCoreID !== preferencesFromDB?.Execution_DefaultReferenceCore,
					);
				}
			} finally {
				this.preferencesFetchStatus.loading = false;
			}
		},
		async getActiveCores(): Promise<Core[]> {
			const fetches: Promise<void>[] = [];
			if (this.remoteCoresList === undefined) {
				fetches.push(this.fetchUserCoresFromRemote());
			}
			if (this.defaultCoresList === undefined) {
				fetches.push(this.fetchDefaultCoresFromRemote());
			}

			await Promise.allSettled(fetches);

			return [...(this.defaultCoresList ?? []), ...(this.remoteCoresList ?? [])];
		},
		deprecatedCoreBuildErrorMessage(coreName: string): string {
			return `The selected core "${coreName}" is deprecated and can no longer be used for builds. Please select a different core before running the build.`;
		},
		async getUnusableCoresByUserTier(): Promise<Core[]> {
			const listOfUnusableCores = (await this.getCoresFilteredCoresByUserTier()).filter(
				(c) => c.Disabled || c.Deprecated,
			);
			return listOfUnusableCores;
		},
		async getCoresFilteredCoresByUserTier(): Promise<Core[]> {
			await this.getActiveCores();
			const userStore = useUserStore();
			const thisUserTierDetails = await userStore.getCurrentUserTierDetails();
			if (userStore.currentUserObjectFromDB === undefined) {
				try {
					await userStore.fetchCurrentUserObjectFromDB();
				} catch {
					// Best effort.
				}
			}
			this.applyAccountCoreCapabilities();
			if (!thisUserTierDetails || this.availableMicroarchitectures.length === 0) {
				this.userTierResolutionFailed =
					userStore.tierResolutionFailed || this.availableMicroarchitectures.length === 0;
				return [] as Core[];
			}
			this.userTierResolutionFailed = false;

			const allowedCores = this.allCoresList.map((core: Core) => {
				const isKnownAndVisibleMicroArchitecture = this.availableMicroarchitectures.some(
					(am) => am.Name === core.Microarchitecture && !am.Disabled,
				);
				const isRetiredCoreClass = retiredCoreClasses.includes(core.Class as CoreClass);
				const isAllowedMicroArchitecture = thisUserTierDetails.allowance.AllowedMicroArchitectures.includes(
					core.Microarchitecture,
				);
				const isReference = core.Microarchitecture == "Reference";
				const precisionLimit = isReference
					? thisUserTierDetails.allowance.MaxReferenceQuality
					: thisUserTierDetails.allowance.MaxPrecisionBits;
				const isAllowedPrecision = core.Precision ? withinLimit(core.Precision, precisionLimit) : true;

				const isAllowedMemory = withinLimit(
					core.MemorySize,
					thisUserTierDetails.allowance.MaximumAttachedProcessorRam,
				);

				const isAllowedCoreClass = thisUserTierDetails.allowance.AllowedCoreClasses.includes(
					core.Class as CoreClass,
				);

				if (!isKnownAndVisibleMicroArchitecture || isRetiredCoreClass) {
					core.Deprecated = true;
					core.Hidden = !isKnownAndVisibleMicroArchitecture || core.Owner === "*";
				}

				if (
					isKnownAndVisibleMicroArchitecture &&
					(!isAllowedMicroArchitecture || !isAllowedPrecision || !isAllowedMemory || !isAllowedCoreClass)
				) {
					core.Disabled = true;
				}

				if (core.Owner === "*") {
					core.Immutable = true;
				}

				return core;
			});

			this.coresFilteredCoresByUserTier = allowedCores;
			return this.coresFilteredCoresByUserTier;
		},
	},
});
